/**
 * affordability-hero-geometry.ts
 *
 * Pure helper that derives the range-bar geometry for the AffordabilityHero
 * from the dashboard's affordability overlay outcome (or from buyingPowerMax
 * alone when the overlay is absent).
 *
 * Two output paths (discriminated union):
 *
 *   overlay   — both bandLow+bandHigh present → band span + qual-price tick + target marker
 *   estimate  — overlay absent, buyingPowerMax > 0 → single estimate fill + optional target
 *   null      — no data at all (missing/zero buyingPowerMax + no overlay) → no figure
 *
 * Invariants (spec §2 / test spec "Affordability hero range bar"):
 *   - Never a fabricated band, never $0.
 *   - Returns null rather than a $0 figure when all inputs are zero/absent.
 *   - All percentages are clamped to [0, 100].
 *   - The axis max always exceeds all data points (headroom so bars never
 *     read as "full").
 */

// ─────────────────────────────────────────────────────────────────────────────
// Input
// ─────────────────────────────────────────────────────────────────────────────

export interface HeroOverlayInput {
  /** Band low (comfortable floor), in dollars. Null when overlay is absent. */
  bandLow: number | null;
  /** Band high (stretch ceiling), in dollars. Null when overlay is absent. */
  bandHigh: number | null;
  /** Sill qualification price, in dollars. Null when overlay is absent. */
  sillQualPrice: number | null;
  /** Max buying power from readiness engine (always present when a snapshot exists). */
  buyingPowerMax: number;
  /** Buyer's current target price in dollars. 0 or absent = no target set. */
  targetPrice: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Output (discriminated union)
// ─────────────────────────────────────────────────────────────────────────────

/** Geometry for the overlay path: band span + qual-price tick + optional target marker. */
export interface HeroGeometryOverlay {
  kind: "overlay";
  /** Left offset of the comfortable→stretch band as % of axis (0–100). */
  bandLeftPct: number;
  /** Width of the band span as % of axis (0–100). */
  bandWidthPct: number;
  /** Qual-price tick position as % of axis (0–100). */
  qualPricePct: number;
  /** Target marker position as % of axis (0–100). Null when no target is set. */
  targetPct: number | null;
  /** Axis maximum value in dollars (used by the component to label the right end). */
  axisMax: number;
  /** Human-readable qual price label, e.g. "$560K". */
  qualPriceDisplay: string;
  /** Human-readable band range label, e.g. "$480K–$560K". */
  bandDisplay: string;
  /** Human-readable target price label. Null when no target is set. */
  targetDisplay: string | null;
}

/** Geometry for the estimate-only path: single fill bar + optional target marker. */
export interface HeroGeometryEstimate {
  kind: "estimate";
  /** Estimate fill bar position as % of axis (0–100). */
  estimatePct: number;
  /** Target marker position as % of axis (0–100). Null when no target is set. */
  targetPct: number | null;
  /** Axis maximum value in dollars. */
  axisMax: number;
  /** Human-readable estimate label, e.g. "$640K". */
  estimateDisplay: string;
  /** Human-readable target price label. Null when no target is set. */
  targetDisplay: string | null;
}

export type HeroGeometry = HeroGeometryOverlay | HeroGeometryEstimate;

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Clamps v to [lo, hi]. */
function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/** Rounds value up to the nearest multiple. */
function ceilTo(value: number, multiple: number): number {
  return Math.ceil(value / multiple) * multiple;
}

/**
 * Formats a dollar amount as a compact currency string.
 * $640,000 → "$640K", $1,200,000 → "$1.2M"
 */
function fmtCompact(dollars: number): string {
  if (dollars >= 1_000_000) {
    const m = dollars / 1_000_000;
    // Use one decimal unless it's a clean integer
    return `$${Number.isInteger(m) ? m : m.toFixed(1)}M`;
  }
  if (dollars >= 1_000) {
    return `$${Math.round(dollars / 1_000)}K`;
  }
  return `$${dollars.toLocaleString()}`;
}

/**
 * Computes the axis max given the highest data point and an optional target.
 * Adds 15% headroom so data bars never reach the right edge.
 * Rounds up to the nearest $50K for a clean axis.
 */
function computeAxisMax(topValue: number, targetDollars: number): number {
  const anchor = targetDollars > 0 ? Math.max(topValue, targetDollars) : topValue;
  return ceilTo(anchor * 1.15, 50_000);
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Derives the affordability hero range-bar geometry from the overlay outcome.
 *
 * Returns null when there is no data to display (missing/zero buyingPowerMax
 * AND no overlay) — the hero renders an estimate-pending presentation instead.
 *
 * Decision tree:
 *   1. bandLow > 0 AND bandHigh > bandLow AND sillQualPrice > 0
 *      → overlay path (band span + qual-price tick)
 *   2. buyingPowerMax > 0
 *      → estimate-only path (single fill bar)
 *   3. otherwise → null (no figure)
 */
export function deriveHeroGeometry(
  input: HeroOverlayInput,
): HeroGeometry | null {
  const { bandLow, bandHigh, sillQualPrice, buyingPowerMax, targetPrice } =
    input;

  const hasValidBand =
    bandLow != null &&
    bandHigh != null &&
    bandLow > 0 &&
    bandHigh > bandLow;

  const hasQualPrice = sillQualPrice != null && sillQualPrice > 0;
  const hasEstimate = buyingPowerMax > 0;
  const hasTarget = targetPrice > 0;

  // ── Overlay path ────────────────────────────────────────────────────────
  if (hasValidBand && hasQualPrice) {
    const lo = bandLow!;
    const hi = bandHigh!;
    const qual = sillQualPrice!;
    const top = Math.max(hi, qual);
    const axisMax = computeAxisMax(top, targetPrice);

    const bandLeftPct = clamp((lo / axisMax) * 100, 0, 100);
    const bandRightPct = clamp((hi / axisMax) * 100, 0, 100);
    const bandWidthPct = clamp(bandRightPct - bandLeftPct, 0, 100 - bandLeftPct);
    const qualPricePct = clamp((qual / axisMax) * 100, 0, 100);
    const targetPct = hasTarget
      ? clamp((targetPrice / axisMax) * 100, 0, 100)
      : null;

    return {
      kind: "overlay",
      bandLeftPct,
      bandWidthPct,
      qualPricePct,
      targetPct,
      axisMax,
      qualPriceDisplay: fmtCompact(qual),
      bandDisplay: `${fmtCompact(lo)}–${fmtCompact(hi)}`,
      targetDisplay: hasTarget ? fmtCompact(targetPrice) : null,
    };
  }

  // ── Overlay with qualPrice but no valid band (partial data) ───────────
  // sillQualPrice present but bandLow/bandHigh absent or invalid → treat as
  // overlay with zero-width band (qualPrice-only tick, no span)
  if (hasQualPrice && !hasValidBand) {
    const qual = sillQualPrice!;
    const axisMax = computeAxisMax(qual, targetPrice);
    const qualPricePct = clamp((qual / axisMax) * 100, 0, 100);
    const targetPct = hasTarget
      ? clamp((targetPrice / axisMax) * 100, 0, 100)
      : null;

    return {
      kind: "overlay",
      bandLeftPct: 0,
      bandWidthPct: 0,
      qualPricePct,
      targetPct,
      axisMax,
      qualPriceDisplay: fmtCompact(qual),
      bandDisplay: fmtCompact(qual),
      targetDisplay: hasTarget ? fmtCompact(targetPrice) : null,
    };
  }

  // ── Estimate-only path ────────────────────────────────────────────────
  if (hasEstimate) {
    const estimate = buyingPowerMax;
    const axisMax = computeAxisMax(estimate, targetPrice);
    const estimatePct = clamp((estimate / axisMax) * 100, 0, 100);
    const targetPct = hasTarget
      ? clamp((targetPrice / axisMax) * 100, 0, 100)
      : null;

    return {
      kind: "estimate",
      estimatePct,
      targetPct,
      axisMax,
      estimateDisplay: fmtCompact(estimate),
      targetDisplay: hasTarget ? fmtCompact(targetPrice) : null,
    };
  }

  // ── No data ───────────────────────────────────────────────────────────
  return null;
}
